import { useState, useCallback } from 'react';
import { useSchool } from '@/contexts/SchoolContext';
import { useAuth } from '@/hooks';
import { workflowEngineService } from '@/services/workflow/WorkflowEngineService';

/**
 * useWorkflowEngine - Start, advance, and cancel workflow instances.
 */
export function useWorkflowEngine() {
  const { schoolId } = useSchool();
  const { user } = useAuth();

  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const startWorkflow = useCallback(async (templateId, context = {}, staffLookup = {}) => {
    if (!schoolId || !user?.uid) return null;

    setStarting(true);
    setError(null);

    try {
      const actor = { uid: user.uid, displayName: user.displayName || user.email };
      const result = await workflowEngineService.startWorkflow(
        schoolId, templateId, context, actor, staffLookup
      );
      return result;
    } catch (err) {
      console.error('[useWorkflowEngine] Start error:', err);
      setError(err.message);
      throw err;
    } finally {
      setStarting(false);
    }
  }, [schoolId, user?.uid, user?.displayName, user?.email]);

  const advanceStep = useCallback(async (instanceId, stepId, staffLookup = {}) => {
    if (!schoolId || !user?.uid) return;

    try {
      const actor = { uid: user.uid, displayName: user.displayName || user.email };
      await workflowEngineService.onStepCompleted(schoolId, instanceId, stepId, actor, staffLookup);
    } catch (err) {
      console.error('[useWorkflowEngine] Advance error:', err);
    }
  }, [schoolId, user?.uid, user?.displayName, user?.email]);

  const cancelWorkflow = useCallback(async (instanceId) => {
    if (!schoolId) return;

    try {
      await workflowEngineService.cancelWorkflow(schoolId, instanceId);
    } catch (err) {
      console.error('[useWorkflowEngine] Cancel error:', err);
      throw err;
    }
  }, [schoolId]);

  return {
    startWorkflow,
    advanceStep,
    cancelWorkflow,
    starting,
    error,
  };
}

export default useWorkflowEngine;
